import { FormProvider } from "react-hook-form";

import { FormFieldGroup } from "../../../components";
import { useOpenCustomerInstructionForm } from "./hooks/useOpenCustomerInstructionForm";
import { flattenOpenCustomerInstruction } from "./OpenCustomerInstructionForm.rules";
import { OpenCustomerInstructionFormProps } from "./OpenCustomerInstructionForm.types";
import { cn } from "@/lib/utils";

// Same field styling as the order form.
const fieldCls =
  "w-full rounded-lg border border-border bg-background px-2.5 py-1.5 text-[12px] text-foreground shadow-sm outline-none transition-colors placeholder:text-muted-foreground focus:border-primary focus:ring-2 focus:ring-primary/15";

function errMsg(error?: { message?: unknown }) {
  return typeof error?.message === "string" ? error.message : undefined;
}

function SubHeading({ title }: { title: string }) {
  return (
    <div className="mb-2.5 flex items-center gap-2">
      <span className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
        {title}
      </span>
      <span className="h-px flex-1 bg-border/70" />
    </div>
  );
}

export function OpenCustomerInstructionForm({
  onDocumentCreated,
  onClose,
  initialData,
}: OpenCustomerInstructionFormProps) {
  const form = useOpenCustomerInstructionForm(initialData);
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = form;

  const onSubmit = (
    data: Parameters<typeof flattenOpenCustomerInstruction>[0]
  ) => {
    onDocumentCreated?.(flattenOpenCustomerInstruction(data));
    onClose?.();
  };

  return (
    <FormProvider {...form}>
      <form
        onSubmit={handleSubmit(onSubmit)}
        className="flex h-full min-h-0 flex-col"
      >
        <div className="min-h-0 flex-1 overflow-y-auto bg-muted/20 p-4 sm:p-5">
          <div className="mx-auto max-w-3xl divide-y divide-border/60 overflow-hidden rounded-xl border border-border/80 bg-card shadow-sm">
            <div className="p-4">
              <SubHeading title="Åpen kundeinstruks" />
              <p className="-mt-1 mb-3 text-[11px] text-muted-foreground">
                Dokumentet er forhåndsutfylt med kundens data. Fyll inn
                overskrift og innhold.
              </p>
              <div className="space-y-3">
                <FormFieldGroup
                  label="Overskrift"
                  error={errMsg(errors.header)}
                >
                  <input
                    type="text"
                    {...register("header")}
                    className={fieldCls}
                  />
                </FormFieldGroup>

                <FormFieldGroup label="Innhold" error={errMsg(errors.main)}>
                  <textarea
                    {...register("main")}
                    rows={10}
                    className={cn(fieldCls, "resize-y")}
                  />
                </FormFieldGroup>
              </div>
            </div>
          </div>

          {Object.keys(errors).length > 0 && (
            <p className="mx-auto mt-3 max-w-3xl text-[11px] font-medium text-destructive">
              Fyll ut alle obligatoriske felt.
            </p>
          )}
        </div>

        <div className="z-10 flex shrink-0 justify-end gap-3 border-t border-border bg-background px-4 py-3">
          {onClose && (
            <button
              type="button"
              onClick={onClose}
              className="rounded-md border border-border px-4 py-2 text-sm font-medium transition-colors hover:bg-muted"
            >
              Avbryt
            </button>
          )}
          <button
            type="submit"
            className="rounded-md bg-primary px-5 py-2 text-sm font-semibold text-primary-foreground shadow-sm transition-colors hover:bg-primary/90"
          >
            {initialData ? "Lagre endringer" : "Legg til dokument"}
          </button>
        </div>
      </form>
    </FormProvider>
  );
}
